import {
    useEffect,
    useState,
} from "react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useLocation,
    useNavigate,
} from "react-router-dom";

import type {
    RootState,
    AppDispatch,
} from "../store";

import {
    setJourney,
    clearJourney,
} from "../store/journeySlice";

import {
    clearAuth,
} from "../store/authSlice";

import {
    getJourney,
} from "../api/journey.api";

import {
    getRouteForPhase,
} from "../utils/journeyRouting";

import LoadingAnimation
    from "./LoadingAnimation";

import { ApiError } from "../api/ApiError";


interface Props {
    children: React.ReactNode;
}


const AuthBootstrap: React.FC<Props> = ({
    children,
}) => {

    const dispatch =
        useDispatch<AppDispatch>();

    const navigate =
        useNavigate();

    const location =
        useLocation();


    const token =
        useSelector(
            (state: RootState) =>
                state.auth.token
        );


    const journey =
        useSelector(
            (state: RootState) =>
                state.journey.journey
        );


    const [loading, setLoading] =
        useState(true);


    /*
     * =========================================================
     * PHASE GUARD
     * =========================================================
     *
     * AuthEntry only checks journey.phase against the route
     * ONCE, at the moment the ?code= link is exchanged.
     *
     * That's not enough: a customer can refresh the page, or
     * open a bookmarked/deep-linked URL (e.g. /ndashboard)
     * at any later point, and AuthBootstrap would previously
     * just rehydrate Redux and render whatever route they
     * landed on — even if their journey.phase says they
     * should still be on /naddress or /napproval.
     *
     * This runs the same phase -> route check on every
     * mount/refresh, using the SAME helper AuthEntry uses,
     * so the two never drift out of sync with each other.
     */
    const enforcePhaseRoute = (
        currentJourney: NonNullable<
            RootState["journey"]["journey"]
        >
    ) => {

        /*
         * =========================================================
         * CALLBACK ROUTES
         * =========================================================
         *
         * External providers redirect the browser back to these
         * routes. The callback component must be allowed to mount
         * and complete its backend processing before any phase
         * based redirect occurs.
         */

        const isExternalCallbackRoute =
            location.pathname.includes("/callback") ||
            location.pathname === "/transactionstatusoffline" ||
            location.pathname === "/downPayment";
        if (isExternalCallbackRoute) {
            return;
        }


        const expectedRoute =
            getRouteForPhase(
                currentJourney.phase
            );


        if (
            location.pathname !== expectedRoute
        ) {

            navigate(
                expectedRoute,
                {
                    replace: true,
                }
            );

        }

    };


    useEffect(() => {

        const restoreSession =
            async () => {

                /*
                 * =========================================
                 * CASE 0:
                 * A FRESH SESSION CODE IS PRESENT
                 * =========================================
                 *
                 * The URL carries a ?code=... that AuthEntry
                 * needs to exchange. AuthEntry alone decides
                 * whether this belongs to the SAME journey as
                 * whatever is currently persisted (safe to
                 * resume) or a DIFFERENT one (must clear the
                 * stale session and force fresh login).
                 *
                 * If we run phase-based enforcement here first,
                 * a leftover token from a PREVIOUS user/journey
                 * can redirect this request away before AuthEntry
                 * ever mounts — silently dropping the code and
                 * landing the new user on the old session.
                 */

                const hasSessionCode =
                    new URLSearchParams(
                        location.search
                    ).has("code");

                if (hasSessionCode) {

                    setLoading(false);

                    return;
                }


                /*
                 * =========================================
                 * CASE 1:
                 * NO JWT
                 * =========================================
                 *
                 * Do absolutely nothing.
                 *
                 * AuthEntry handles:
                 *
                 * ?code=...
                 *      ↓
                 * exchangeSessionCode()
                 *      ↓
                 * /Nlogin
                 */

                if (!token) {

                    setLoading(false);

                    return;
                }


                /*
                 * =========================================
                 * CASE 2:
                 * JWT EXISTS + JOURNEY EXISTS
                 * =========================================
                 *
                 * Existing authenticated session — this is
                 * the "refresh the page" / "deep link while
                 * still logged in" case. Enforce phase route
                 * here.
                 */

                if (journey) {

                    enforcePhaseRoute(journey);

                    setLoading(false);

                    return;
                }


                /*
                 * =========================================
                 * CASE 3:
                 * JWT EXISTS BUT JOURNEY IS MISSING
                 * =========================================
                 *
                 * Usually happens after browser refresh
                 * with an empty in-memory Redux store.
                 */

                try {

                    const response =
                        await getJourney();


                    dispatch(
                        setJourney(
                            response.data
                        )
                    );

                    enforcePhaseRoute(
                        response.data
                    );

                } catch (error) {

                    if (
                        error instanceof ApiError &&
                        (
                            error.status === 401 ||
                            error.status === 403
                        )
                    ) {

                        dispatch(clearAuth());
                        dispatch(clearJourney());

                        navigate(
                            "/Nlogin",
                            {
                                replace: true,
                            }
                        );

                        return;
                    }

                    /*
                     * 400/404/409, 500+, network errors, etc.
                     * Do NOT clear an otherwise valid authentication
                     * session because of a temporary/unrelated failure.
                     */

                } finally {

                    setLoading(false);

                }

            };


        restoreSession();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        token,
        journey,
        dispatch,
        location.search,
    ]);


    if (loading) {

        return (
            <LoadingAnimation
                message="Restoring your journey..."
            />
        );

    }


    return <>{children}</>;

};


export default AuthBootstrap;